/**
 * `AIMessage` — renders one assistant-role message by dispatching each
 * AI-SDK v5 part to the right child component. Text parts go through
 * Streamdown for progressive markdown rendering; reasoning parts get
 * the collapsible "thinking" panel; tool parts get `ToolCallMessage`;
 * artifact-shaped tool outputs upgrade into an `ArtifactPreview` card.
 *
 * Ports the assembly logic from Studio's `message.svelte`. The order
 * of parts is preserved exactly — the model's interleaving of text →
 * tool-call → tool-output → text is meaningful for narration.
 *
 * Layout strategy:
 *   - Outer container uses `gap-3` (12 px) between all top-level children.
 *   - Consecutive tool/reasoning parts are collapsed into a single
 *     "compact cluster" div with `gap-1.5` (6 px) between rows.
 *     This mirrors how Claude.ai and ChatGPT group tool activity — the
 *     rows read as one visual unit rather than a spread-out list.
 *   - Prose text parts get `[&_p]:my-2` to reduce Tailwind Typography's
 *     default 1.25 em paragraph margin (~20 px) to 8 px, preventing the
 *     excessive blank-line spacing between short bold paragraphs.
 */
import type { UIMessage, UIMessagePart, UIDataTypes, UITools } from "ai";
import { MessageResponse, cn } from "@flint/ui";
import { ReasoningMessage } from "./reasoning-message.tsx";
import { ToolCallMessage } from "./tool-call-message.tsx";
import { ArtifactPreview } from "./artifact-preview.tsx";
import { GeneratedFileAttachment } from "./generated-file-attachment.tsx";

interface AIMessageProps {
  message: UIMessage;
  isLoading?: boolean;
}

// A part is "compact" (tool row or reasoning toggle) when it should sit
// tightly against its neighbours rather than taking a full prose-block gap.
function isCompactPart(part: UIMessagePart<UIDataTypes, UITools>): boolean {
  return (
    part.type.startsWith("tool-") ||
    part.type === "reasoning" ||
    part.type === "dynamic-tool"
  );
}

type PartGroup =
  | { kind: "content"; part: UIMessagePart<UIDataTypes, UITools>; index: number }
  | { kind: "compact"; parts: Array<{ part: UIMessagePart<UIDataTypes, UITools>; index: number }> };

function groupParts(parts: UIMessagePart<UIDataTypes, UITools>[]): PartGroup[] {
  const groups: PartGroup[] = [];
  for (let i = 0; i < parts.length; i++) {
    const part = parts[i]!;
    if (isCompactPart(part)) {
      const last = groups[groups.length - 1];
      if (last?.kind === "compact") {
        last.parts.push({ part, index: i });
      } else {
        groups.push({ kind: "compact", parts: [{ part, index: i }] });
      }
    } else {
      groups.push({ kind: "content", part, index: i });
    }
  }
  return groups;
}

export function AIMessage({ message, isLoading }: AIMessageProps) {
  // `min-w-0` is load-bearing — see comment in PartRenderer for the full
  // explanation. Without it wide markdown tables / pre blocks push the
  // column past max-w-3xl and break the workspace layout.
  const groups = groupParts(message.parts);

  return (
    <div className="flex w-full min-w-0 flex-col gap-4 text-base leading-7 text-ink">
      {groups.map((group, groupIndex) => {
        if (group.kind === "compact") {
          return (
            <div key={`${message.id}-cg-${groupIndex}`} className="flex flex-col gap-1.5">
              {group.parts.map(({ part, index }) => (
                <PartRenderer
                  key={`${message.id}-${index}`}
                  part={part}
                  isMessageStreaming={isLoading ?? false}
                />
              ))}
            </div>
          );
        }
        return (
          <PartRenderer
            key={`${message.id}-${group.index}`}
            part={group.part}
            isMessageStreaming={isLoading ?? false}
          />
        );
      })}
    </div>
  );
}

interface PartRendererProps {
  part: UIMessagePart<UIDataTypes, UITools>;
  isMessageStreaming: boolean;
}

function PartRenderer({ part, isMessageStreaming }: PartRendererProps) {
  // Text — the assistant's prose, streamed token by token. Streamdown
  // renders progressively so each delta paints immediately.
  //
  // `[&_p]:my-2` overrides Tailwind Typography's default 1.25 em paragraph
  // margin (~20 px each side → ~40 px between paragraphs). Chat responses
  // with multiple short bold paragraphs look over-spaced at that default.
  // 8 px each side keeps paragraphs readable without feeling like a blog.
  //
  // `min-w-0`/`max-w-full` + per-element overflow rules are load-bearing:
  // wide markdown tables and `pre` blocks have intrinsic widths that would
  // otherwise blow out the column past max-w-3xl.
  if (part.type === "text") {
    if (!part.text?.trim()) return null;
    return (
      <MessageResponse
        className={cn(
          "prose min-w-0 max-w-none leading-[1.7]",
          "[&_p]:leading-[1.7] [&_p]:my-2",
          "[&_li]:leading-[1.7]",
          "[&_table]:block [&_table]:max-w-full [&_table]:overflow-x-auto [&_table]:min-w-full",
          "[&_pre]:max-w-full [&_pre]:overflow-x-auto [&_pre]:whitespace-pre-wrap [&_pre]:break-words",
        )}
      >
        {part.text}
      </MessageResponse>
    );
  }

  // Reasoning — collapsible "thinking" panel. v5 parts use `text` +
  // `state`; we also tolerate v4-shaped persisted parts that nested
  // text under `details[]`.
  if (part.type === "reasoning") {
    const reasoningText =
      typeof part.text === "string"
        ? part.text
        : extractLegacyReasoningText(part as unknown as { details?: Array<{ text?: string }> });
    const state = (part as { state?: "streaming" | "done" }).state;
    return (
      <ReasoningMessage
        text={reasoningText}
        isStreaming={state === "streaming" && isMessageStreaming}
      />
    );
  }

  // Tool calls — v5 emits `tool-${name}` parts; v4-shaped persisted
  // parts use a literal `tool-invocation` type. `ToolCallMessage`
  // accepts both. If the output looks artifact-shaped, upgrade the
  // render to the dedicated preview card.
  if (part.type.startsWith("tool-") || part.type === "dynamic-tool") {
    const toolPart = part as unknown as {
      type: string;
      toolName?: string | undefined;
      output?: unknown;
      result?: unknown;
    };
    const output = toolPart.output ?? toolPart.result;
    const file = detectGeneratedFile(output);
    if (file) {
      return (
        <GeneratedFileAttachment
          title={file.title}
          url={file.url}
          format={file.format}
          mediaType={file.mediaType}
          sizeBytes={file.sizeBytes}
        />
      );
    }
    const artifact = detectArtifact(output);
    if (artifact) {
      return (
        <ArtifactPreview
          title={artifact.title}
          description={artifact.description}
          kind={artifact.kind}
          body={artifact.body}
        />
      );
    }
    return <ToolCallMessage part={toolPart as never} isMessageStreaming={isMessageStreaming} />;
  }

  // File parts on the assistant side are rare for Flint today — render
  // a download/open attachment instead of a bare label.
  if (part.type === "file") {
    const filePart = part as { filename?: string; mediaType?: string; url?: string };
    return (
      <GeneratedFileAttachment
        title={filePart.filename ?? "Attachment"}
        url={filePart.url}
        mediaType={filePart.mediaType}
      />
    );
  }

  // step-start, source-url, source-document, data-* — silently ignored.
  // Data parts (e.g. our `data-title`) ride through `useChat.onData`
  // instead of the message renderer.
  return null;
}

function extractLegacyReasoningText(part: { details?: Array<{ text?: string }> }): string {
  if (!Array.isArray(part.details)) return "";
  return part.details
    .map((detail) => (typeof detail.text === "string" ? detail.text : ""))
    .join("\n");
}

/**
 * Heuristic for "this tool produced an artifact". We don't yet have a
 * formal artifact contract in the Flint domain, so we sniff for the
 * obvious fields. When a real artifacts module ships, this dispatch
 * moves to a typed discriminator on the tool output.
 */
function detectArtifact(output: unknown): {
  title: string;
  description?: string | undefined;
  kind: "document" | "table" | "chart" | "kpi" | "unknown";
  body: unknown;
} | null {
  if (!output || typeof output !== "object") return null;
  const candidate = output as Record<string, unknown>;
  if (typeof candidate.artifact !== "object" || candidate.artifact === null) return null;
  const artifact = candidate.artifact as Record<string, unknown>;
  if (typeof artifact.title !== "string") return null;

  const rawKind = typeof artifact.kind === "string" ? artifact.kind : "unknown";
  const kind = (["document", "table", "chart", "kpi"] as const).includes(rawKind as never)
    ? (rawKind as "document" | "table" | "chart" | "kpi")
    : "unknown";

  return {
    title: artifact.title,
    description: typeof artifact.description === "string" ? artifact.description : undefined,
    kind,
    body: artifact.body ?? artifact.data ?? candidate.preview,
  };
}

function detectGeneratedFile(output: unknown): {
  title: string;
  url?: string | undefined;
  format?: string | undefined;
  mediaType?: string | undefined;
  sizeBytes?: number | undefined;
} | null {
  if (!output || typeof output !== "object") return null;
  const candidate = output as Record<string, unknown>;
  const source =
    typeof candidate.file === "object" && candidate.file !== null
      ? (candidate.file as Record<string, unknown>)
      : candidate;

  const url = typeof source.url === "string" ? source.url : undefined;
  const key = typeof source.key === "string" ? source.key : undefined;
  const format = typeof source.format === "string" ? source.format : undefined;
  const mediaType = typeof source.mediaType === "string" ? source.mediaType : undefined;
  const sizeBytes = typeof source.sizeBytes === "number" ? source.sizeBytes : undefined;
  const hasFileSignal =
    typeof candidate.file === "object" ||
    (typeof candidate.ok === "boolean" &&
      (typeof url === "string" || typeof key === "string") &&
      typeof format === "string");

  if (!hasFileSignal) return null;

  const artifact =
    typeof candidate.artifact === "object" && candidate.artifact !== null
      ? (candidate.artifact as Record<string, unknown>)
      : undefined;
  const title =
    (typeof source.title === "string" && source.title) ||
    (typeof source.filename === "string" && source.filename) ||
    (typeof artifact?.title === "string" && `${artifact.title}.${extensionFor(format)}`) ||
    (key ? key.split("/").pop() : undefined) ||
    "Generated file";

  return { title, url, format, mediaType, sizeBytes };
}

function extensionFor(format?: string): string {
  if (format === "markdown") return "md";
  return format ?? "file";
}
